import React, { useEffect, useRef, useState } from 'react';
import * as fabric from 'fabric';
import {
  SelectIcon,
  TextIcon,
  BrushIcon,
  EraserIcon,
  RectangleIcon,
  CircleIcon,
  TriangleIcon,
  ImageIcon,
  TrashIcon,
  SaveIcon,
  LayersIcon,
  EyeIcon,
  EyeOffIcon,
  LockIcon,
  UnlockIcon
} from './icons';

interface ImageEditorProps {
  initialImage?: string;
  onSave?: (dataUrl: string) => void;
}

type Tool = 'select' | 'text' | 'eraser' | 'image' | 'brush' | 'shape';

interface Layer {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  object: fabric.Object;
}

const ImageEditorComponent: React.FC<ImageEditorProps> = ({ initialImage, onSave }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const [activeTool, setActiveTool] = useState<Tool>('select');
  const [layers, setLayers] = useState<Layer[]>([]);
  const [selectedFont, setSelectedFont] = useState<string>('Arial');
  const [fontSize, setFontSize] = useState<number>(20);
  const [textColor, setTextColor] = useState<string>('#000000');
  const [brushSize, setBrushSize] = useState<number>(5);
  const [showLayersPanel, setShowLayersPanel] = useState<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fonts = [
    'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana', 
    'Courier New', 'Impact', 'Comic Sans MS', 'Tahoma', 'Trebuchet MS'
  ];

  useEffect(() => {
    if (canvasRef.current) {
      const fabricCanvas = new fabric.Canvas(canvasRef.current, {
        width: 800,
        height: 600,
        backgroundColor: '#ffffff',
        preserveObjectStacking: true
      });

      if (initialImage) {
        (fabric.Image as any).fromURL(initialImage, (img: fabric.Image) => {
          const layer: Layer = {
            id: `image-${Date.now()}`,
            name: 'Фоновое изображение',
            visible: true,
            locked: false,
            object: img
          };
          fabricCanvas.add(img);
          setLayers([layer]);
          fabricCanvas.renderAll();
        });
      }

      setCanvas(fabricCanvas);

      // Добавляем обработчики событий для обновления слоев
      fabricCanvas.on('object:added', updateLayers);
      fabricCanvas.on('object:removed', updateLayers);
      fabricCanvas.on('object:modified', updateLayers);

      return () => {
        fabricCanvas.dispose();
      };
    }
  }, [initialImage]);

  const updateLayers = () => {
    if (!canvas) return;
    
    const newLayers: Layer[] = canvas.getObjects().map((obj, index) => ({
      id: (obj as any).name || `layer-${index}`,
      name: (obj as any).name || `Слой ${index + 1}`,
      visible: obj.visible !== false,
      locked: obj.selectable === false,
      object: obj
    }));
    
    setLayers(newLayers);
  };

  const handleToolChange = (tool: Tool) => {
    if (!canvas) return;
    
    setActiveTool(tool);
    canvas.isDrawingMode = false;
    canvas.selection = tool === 'select';
    
    if (tool === 'eraser') {
      const eraserBrush = new (fabric as any).EraseBrush(canvas);
      eraserBrush.width = brushSize;
      canvas.freeDrawingBrush = eraserBrush;
      canvas.isDrawingMode = true;
    } else if (tool === 'brush') {
      const brush = new fabric.PencilBrush(canvas);
      brush.width = brushSize;
      brush.color = textColor;
      canvas.freeDrawingBrush = brush;
      canvas.isDrawingMode = true;
    }
  };

  const handleAddText = () => {
    if (!canvas) return;

    const text = new fabric.IText('Двойной клик для редактирования', {
      left: 100,
      top: 100,
      fontSize: fontSize,
      fontFamily: selectedFont,
      fill: textColor,
      name: `text-${Date.now()}`
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    updateLayers();
    canvas.renderAll();
  };

  const handleAddShape = (shapeType: 'rect' | 'circle' | 'triangle') => {
    if (!canvas) return;

    let shape: fabric.Object;
    const commonProps = {
      left: 100,
      top: 100,
      fill: textColor,
      name: `${shapeType}-${Date.now()}`
    };

    switch (shapeType) {
      case 'rect':
        shape = new fabric.Rect({ ...commonProps, width: 100, height: 100 });
        break;
      case 'circle':
        shape = new fabric.Circle({ ...commonProps, radius: 50 });
        break;
      case 'triangle':
        shape = new fabric.Triangle({ ...commonProps, width: 100, height: 100 });
        break;
      default:
        return;
    }

    canvas.add(shape);
    canvas.setActiveObject(shape);
    updateLayers();
    canvas.renderAll();
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canvas || !event.target.files?.[0]) return;

    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      if (!e.target?.result) return;

      (fabric.Image as any).fromURL(e.target.result as string, (img: fabric.Image) => {
        img.scaleToWidth(200);
        (img as any).name = `image-${Date.now()}`;
        canvas.add(img);
        updateLayers();
        canvas.renderAll();
      });
    };

    reader.readAsDataURL(file);
  };

  const toggleLayerVisibility = (layerId: string) => {
    if (!canvas) return;
    
    const layer = layers.find(l => l.id === layerId);
    if (layer) {
      layer.object.visible = !layer.object.visible;
      layer.visible = layer.object.visible;
      setLayers([...layers]);
      canvas.renderAll();
    }
  };

  const toggleLayerLock = (layerId: string) => {
    if (!canvas) return;
    
    const layer = layers.find(l => l.id === layerId);
    if (layer) {
      layer.object.selectable = layer.object.selectable === false;
      layer.locked = layer.object.selectable === false;
      setLayers([...layers]);
    }
  };

  const deleteLayer = (layerId: string) => {
    if (!canvas) return;
    
    const layer = layers.find(l => l.id === layerId);
    if (layer) {
      canvas.remove(layer.object);
      updateLayers();
      canvas.renderAll();
    }
  };

  const handleSave = () => {
    if (!canvas || !onSave) return;
    const dataUrl = canvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: 1
    });
    onSave(dataUrl);
  };

  const clearCanvas = () => {
    if (!canvas) return;
    canvas.clear();
    setLayers([]);
    canvas.backgroundColor = '#ffffff';
    canvas.renderAll();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'white' }}>
      {/* Панель инструментов */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        padding: '12px', 
        borderBottom: '1px solid #e5e7eb', 
        backgroundColor: '#f9fafb' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: activeTool === 'select' ? '#3b82f6' : 'white',
              color: activeTool === 'select' ? 'white' : 'black',
              border: activeTool === 'select' ? 'none' : '1px solid #d1d5db'
            }}
            onClick={() => handleToolChange('select')}
            title="Выделение"
          >
            <SelectIcon />
          </button>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: activeTool === 'text' ? '#3b82f6' : 'white',
              color: activeTool === 'text' ? 'white' : 'black',
              border: activeTool === 'text' ? 'none' : '1px solid #d1d5db'
            }}
            onClick={() => {
              handleToolChange('text');
              handleAddText();
            }}
            title="Текст"
          >
            <TextIcon />
          </button>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: activeTool === 'brush' ? '#3b82f6' : 'white',
              color: activeTool === 'brush' ? 'white' : 'black',
              border: activeTool === 'brush' ? 'none' : '1px solid #d1d5db'
            }}
            onClick={() => handleToolChange('brush')}
            title="Кисть"
          >
            <BrushIcon />
          </button>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: activeTool === 'eraser' ? '#3b82f6' : 'white',
              color: activeTool === 'eraser' ? 'white' : 'black',
              border: activeTool === 'eraser' ? 'none' : '1px solid #d1d5db'
            }}
            onClick={() => handleToolChange('eraser')}
            title="Ластик"
          >
            <EraserIcon />
          </button>
        </div>

        <div style={{ width: '1px', height: '24px', backgroundColor: '#d1d5db', margin: '0 8px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={() => handleAddShape('rect')}
            title="Прямоугольник"
          >
            <RectangleIcon />
          </button>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={() => handleAddShape('circle')}
            title="Круг"
          >
            <CircleIcon />
          </button>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={() => handleAddShape('triangle')}
            title="Треугольник"
          >
            <TriangleIcon />
          </button>
        </div>

        <div style={{ width: '1px', height: '24px', backgroundColor: '#d1d5db', margin: '0 8px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={() => fileInputRef.current?.click()}
            title="Добавить изображение"
          >
            <ImageIcon />
          </button>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={clearCanvas}
            title="Очистить"
          >
            <TrashIcon />
          </button>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#10b981',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onClick={handleSave}
            title="Сохранить"
          >
            <SaveIcon />
          </button>
        </div>

        <div style={{ marginLeft: 'auto' }}>
          <button
            style={{
              padding: '8px',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: showLayersPanel ? '#3b82f6' : 'white',
              color: showLayersPanel ? 'white' : 'black',
              border: showLayersPanel ? 'none' : '1px solid #d1d5db'
            }}
            onClick={() => setShowLayersPanel(!showLayersPanel)}
            title="Панель слоев"
          >
            <LayersIcon />
          </button>
        </div>
      </div>

      {/* Основная область */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Холст */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          backgroundColor: '#f3f4f6', 
          padding: '16px' 
        }}>
          <div style={{ 
            border: '1px solid #d1d5db', 
            borderRadius: '8px', 
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', 
            backgroundColor: 'white' 
          }}>
            <canvas ref={canvasRef} />
          </div>
        </div>

        {/* Панель слоев */}
        {showLayersPanel && (
          <div style={{ width: '256px', borderLeft: '1px solid #e5e7eb', backgroundColor: 'white' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Слои</h3>
            </div>
            <div style={{ height: '256px', overflowY: 'auto' }}>
              <div style={{ padding: '8px' }}>
                {layers.map((layer) => (
                  <div
                    key={layer.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px',
                      borderRadius: '6px',
                      marginBottom: '4px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                      <button
                        style={{
                          padding: '4px',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onClick={() => toggleLayerVisibility(layer.id)}
                        title={layer.visible ? 'Скрыть' : 'Показать'}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {layer.visible ? (
                          <EyeIcon />
                        ) : (
                          <EyeOffIcon />
                        )}
                      </button>
                      <button
                        style={{
                          padding: '4px',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onClick={() => toggleLayerLock(layer.id)}
                        title={layer.locked ? 'Разблокировать' : 'Заблокировать'}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {layer.locked ? (
                          <LockIcon />
                        ) : (
                          <UnlockIcon />
                        )}
                      </button>
                      <span style={{ 
                        fontSize: '14px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                      }}>
                        {layer.name}
                      </span>
                    </div>
                    <button
                      style={{
                        padding: '4px',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        color: '#dc2626'
                      }}
                      onClick={() => deleteLayer(layer.id)}
                      title="Удалить"
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Панель свойств */}
      <div style={{ 
        borderTop: '1px solid #e5e7eb', 
        backgroundColor: 'white', 
        padding: '16px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '64px' }}>Шрифт:</label>
            <select
              value={selectedFont}
              onChange={(e) => setSelectedFont(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              {fonts.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '80px' }}>Размер:</label>
            <input
              type="range"
              min="8"
              max="72"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              style={{ width: '96px' }}
            />
            <span style={{ fontSize: '14px', color: '#6b7280', minWidth: '32px' }}>{fontSize}px</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '48px' }}>Цвет:</label>
            <input
              type="color"
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              style={{
                width: '40px',
                height: '32px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                cursor: 'pointer',
                padding: 0
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '80px' }}>Кисть:</label>
            <input
              type="range"
              min="1"
              max="50"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              style={{ width: '96px' }}
            />
            <span style={{ fontSize: '14px', color: '#6b7280', minWidth: '32px' }}>{brushSize}px</span>
          </div>
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleImageUpload}
      />
    </div>
  );
};

export const ImageEditor = ImageEditorComponent;